import React from 'react'
import { Skeleton } from 'antd'
import { ExclamationOutlined } from '@ant-design/icons'
import { MIN_LITERS } from './constants'
import { IWogPay } from './useWogPay'
import StationStep from './StationStep'
import AmountStep from './AmountStep'
import ResultStep from './ResultStep'
import WalletField, { IFWalletField } from './WalletField'

interface IFRefuelBody {
  pay: IWogPay
  /** The page for app users puts a wallet picker in front of the stations. */
  walletPicker?: IFWalletField
  /** False on the page for app users: FuelData already narrows the leftovers to the phone's own account, so there is nothing to pick. */
  accountPicker?: boolean
}

const Warning: React.FC<{ title: string; text: string }> = ({ title, text }) => (
  <div className="refuel-status">
    <div className="refuel-status__icon warn">
      <ExclamationOutlined />
    </div>
    <div className="refuel-status__title">{title}</div>
    <div className="refuel-status__text">{text}</div>
  </div>
)

/**
 * The screen of the current step, together with the loading and failure
 * states the step itself does not draw. The modal and the page for app users
 * share it.
 */
const RefuelBody: React.FC<IFRefuelBody> = ({
  pay,
  walletPicker,
  accountPicker = true,
}) => {
  const { account, station, result } = pay

  if (pay.step === 'station') {
    const step = pay.loadingData ? (
      <Skeleton active paragraph={{ rows: 6 }} />
    ) : pay.dataError ? (
      <Warning title="Не вдалося завантажити дані заправки" text={pay.dataError} />
    ) : (
      <StationStep
        accounts={pay.accounts}
        account={account}
        onAccount={pay.chooseAccount}
        accountPicker={accountPicker}
        stations={pay.stations}
        filtered={pay.filtered}
        query={pay.query}
        searching={pay.searching}
        onQuery={pay.setQuery}
        station={station}
        onStation={pay.chooseStation}
        nearby={pay.nearby}
        locked={!!walletPicker && !walletPicker.wallet}
      />
    )

    if (!walletPicker) return step

    // The picker stays put while the wallet's data is being (re)loaded below it.
    return (
      <div className="refuel-step">
        <WalletField {...walletPicker} />
        {step}
      </div>
    )
  }

  if (pay.step === 'amount') {
    if (!station || !account) return null

    if (pay.configError) {
      return (
        <Warning
          title="Не вдалося отримати конфігурацію АЗК"
          text={pay.configError}
        />
      )
    }

    if (pay.blocked) {
      return (
        <Warning
          title={
            pay.hasNozzles
              ? 'На цій АЗК немає доступного пального'
              : 'АЗК не повернула жодної колонки'
          }
          text={
            pay.hasNozzles
              ? `Жоден вид пального на колонках цієї АЗК не збігається із залишками акаунта, або його лишилось менше ${MIN_LITERS} л. Оберіть іншу АЗК.`
              : 'Конфігурація АЗК порожня. Оберіть іншу АЗК.'
          }
        />
      )
    }

    return (
      <AmountStep
        station={station}
        account={account}
        loading={pay.loadingConfig}
        dispensers={pay.dispensers}
        dispenser={pay.dispenser}
        onDispenser={pay.chooseDispenser}
        fuels={pay.fuels}
        fuel={pay.fuel}
        onFuel={pay.chooseFuel}
        amount={pay.amount}
        onAmount={pay.setAmount}
        max={pay.max}
        validation={pay.validation}
        onTemplate={pay.applyTemplate}
      />
    )
  }

  if (!result) return null

  return (
    <ResultStep
      result={result}
      account={account?.name || ''}
      canRetry={pay.canRetry}
    />
  )
}

export default RefuelBody
